import * as React from 'react';

import { theme as ads_theme_agoda } from '@drone-js/theme-agoda';
import { theme as ads_theme_japanican } from '@drone-js/theme-japanican';
import { DesignSystemContextProvider, DesignSystemContext, Direction } from '@drone-js/core';

import '@drone-js/theme/dist/main.css';
import '@drone-js/theme/dist/themes.css';
import '../../themes/base_styles.scss';
import { isRtl } from '@v2/utils/isRtl';
import { useAppContext } from '@contexts/app/hooks/useAppContext';
import { useIsJTB } from '@contexts/app/hooks/useIsJTB';
import { WhitelabelId } from '@whitelabel/WhitelabelId';
import { useExperiment } from '@contexts/experiment/useExperiment';
import { universalLoginExperiment } from '@experiments/universalLogin';

export function withThemeProvider<TProps>(Component: React.FC<TProps>): React.FC<React.PropsWithRef<TProps>> {
    const MemoizedComponent = React.memo<React.FC<TProps>>(Component);

    const ComponentWithThemeProvider: React.FC<React.PropsWithRef<TProps>> = props => {
        const { whitelabelId, appId } = useAppContext();
        const getWlTheme = () => {
            switch (whitelabelId) {
                case WhitelabelId.JTB:
                case WhitelabelId.JTBUAT:
                case WhitelabelId.JAPANICAN:
                case WhitelabelId.JAPANICANUAT:
                    return ads_theme_japanican;
                default:
                    return ads_theme_agoda;
            }
        };

        // TODO: Remove and rever to experiment when wallet is LIVE
        const isWalletAppid = appId === 'wallet';

        const experiments = useExperiment(universalLoginExperiment);
        const isZoomFontSizeFix = isWalletAppid || experiments.ZOOM_FONT_SIZE_FIX;

        const droneCtx: DesignSystemContext = {
            direction: isRtl() ? Direction.RTL : Direction.LTR,
            experiment: {
                isZoomFontSizeFix,
                cssArchitecture: false,
                gridSizeFallback: false,
                alwaysMountTooltip: false,
                inputTrailingAssetPaddingFix: false,
                isMigratingNotificationToAccessibleColor: false,
                isMigratingChipToAccessibleColor: false,
                isMigratingBadgeToAccessibleColor: false,
                deprecateInaccessibleColor: false,
                isToggleButtonAccessibilityColorFix: false,
                iconsMigrationToV3: true,
                isGetDroneContextFromWindow: false,
                radioCheckboxA11yContrastFix: false,
                searchFieldMinHeight: false,
                incorrectInertAttrOnCarouselWhenResizing: false,
                radioCheckboxFocusStateContrastFix: false,
                carouselMigrationToV3: false,
                rurubuAccessibleColor: false,
                japanicanAccessibleColor: false,
                inputV2A11yBorderContrastFix: false,
            },
            theme: getWlTheme(),
        };

        return (
            <DesignSystemContextProvider value={droneCtx}>
                <MemoizedComponent {...props} />
            </DesignSystemContextProvider>
        );
    };

    ComponentWithThemeProvider.displayName = `${Component.displayName || 'Component'}WithDroneThemeProvider`;
    return ComponentWithThemeProvider;
}
